/**
 * App shell: sidebar + header + outlet. Replaces the old AppShell.
 * Wires command palette (⌘K), drag overlay (object-browser routes),
 * and the dev-only Tweaks panel.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Brand, Breadcrumb, type BreadcrumbItem, Icon, Kbd } from './brand';
import { CommandPalette } from './CommandPalette';
import { DragOverlay } from './DragOverlay';
import { TweaksPanel, useTweaks } from './tweaks';

export function AppShell() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tweaks, setTweak] = useTweaks();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const onObjectBrowser = /^\/accounts\/[^/]+\/buckets\/[^/]+/.test(location.pathname);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (
        e.key === '/' &&
        !(
          e.target instanceof HTMLElement &&
          (e.target.matches('input, textarea, select') || e.target.isContentEditable)
        )
      ) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!onObjectBrowser) return;
    let counter = 0;
    const onEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        counter += 1;
        setDragging(true);
      }
    };
    const onLeave = () => {
      counter -= 1;
      if (counter <= 0) {
        counter = 0;
        setDragging(false);
      }
    };
    const onDrop = (e: DragEvent) => {
      counter = 0;
      setDragging(false);
      e.preventDefault();
    };
    const onOver = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [onObjectBrowser]);

  const crumbs = buildBreadcrumb(location.pathname);

  return (
    <div className="bm-app">
      <Sidebar
        currentPath={location.pathname}
        userEmail={user?.email ?? user?.preferred_username ?? user?.sub ?? 'guest'}
        userPicture={user?.picture ?? null}
        isAdmin={isAdmin}
        onSignOut={() => {
          void logout();
        }}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <div className="bm-main">
        <Header
          theme={tweaks.theme}
          onToggleTheme={() => setTweak('theme', tweaks.theme === 'light' ? 'dark' : 'light')}
          breadcrumb={<Breadcrumb items={crumbs} onClick={(to) => navigate(to)} />}
        />
        <main className="bm-content">
          <Outlet />
        </main>
      </div>

      <DragOverlay visible={onObjectBrowser && dragging} />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onToggleTheme={() => setTweak('theme', tweaks.theme === 'light' ? 'dark' : 'light')}
        onToggleDensity={() =>
          setTweak('density', tweaks.density === 'compact' ? 'comfortable' : 'compact')
        }
      />

      {import.meta.env.DEV && (
        <TweaksPanel
          tweaks={tweaks}
          setTweak={setTweak}
          onOpenPalette={() => setPaletteOpen(true)}
        />
      )}
    </div>
  );
}

/* ─── Sidebar ───────────────────────────────────────────────────────── */

interface SidebarProps {
  currentPath: string;
  userEmail: string;
  userPicture: string | null;
  isAdmin: boolean;
  onSignOut: () => void;
  onOpenPalette: () => void;
}

function Sidebar({
  currentPath,
  userEmail,
  userPicture,
  isAdmin,
  onSignOut,
  onOpenPalette,
}: SidebarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [prevPicture, setPrevPicture] = useState(userPicture);
  if (prevPicture !== userPicture) {
    setPrevPicture(userPicture);
    setImgFailed(false);
  }
  const showImg = !!userPicture && !imgFailed;
  const initial = (userEmail || '?').slice(0, 1).toUpperCase();
  return (
    <aside className="bm-sidebar">
      <div className="bm-sidebar-top">
        <NavLink to="/" style={{ display: 'contents' }}>
          <Brand size={22} />
        </NavLink>
      </div>

      <nav className="bm-sidebar-nav">
        <NavRow icon="dashboard" label="Dashboard" to="/dashboard" currentPath={currentPath} />
        <NavRow icon="accounts" label="Accounts" to="/accounts" currentPath={currentPath} />
        <NavRow icon="activity" label="Recent activity" to="/activity" currentPath={currentPath} />

        {isAdmin && (
          <>
            <div className="bm-nav-section">Admin</div>
            <NavRow
              icon="settings"
              label="Settings"
              to="/admin/settings"
              currentPath={currentPath}
            />
            <NavRow icon="audit" label="Audit log" to="/admin/audit" currentPath={currentPath} />
            <NavRow icon="key" label="API keys" to="/admin/api-keys" currentPath={currentPath} />
          </>
        )}
      </nav>

      <button
        type="button"
        className="bm-sidebar-search"
        onClick={onOpenPalette}
        title="Command palette"
      >
        <Icon.search size={14} />
        <span style={{ flex: 1, textAlign: 'left', color: 'var(--bm-text-muted)' }}>Jump to…</span>
        <Kbd>⌘K</Kbd>
      </button>

      <div className="bm-sidebar-user">
        <div className="bm-avatar">
          {showImg ? (
            <img
              src={userPicture as string}
              alt=""
              referrerPolicy="no-referrer"
              onError={() => setImgFailed(true)}
            />
          ) : (
            initial
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {userEmail}
          </div>
          <div style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>
            {isAdmin ? 'admin · writable' : 'operator'}
          </div>
        </div>
        <button type="button" className="bm-btn bm-btn-icon" title="Sign out" onClick={onSignOut}>
          <Icon.signout size={14} />
        </button>
      </div>
    </aside>
  );
}

interface NavRowProps {
  icon: keyof typeof Icon;
  label: string;
  to: string;
  currentPath: string;
  end?: boolean;
}

function NavRow({ icon, label, to, end }: NavRowProps) {
  const IconCmp = Icon[icon];
  return (
    <NavLink to={to} end={end} className="bm-nav-item">
      <IconCmp size={16} />
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
    </NavLink>
  );
}

/* ─── Header ────────────────────────────────────────────────────────── */

interface HeaderProps {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  breadcrumb: ReactNode;
}

function Header({ theme, onToggleTheme, breadcrumb }: HeaderProps) {
  return (
    <header className="bm-header">
      <div className="bm-header-left">{breadcrumb}</div>
      <div className="bm-header-right">
        <span className="bm-status-dot" title="connected" />
        <span style={{ fontSize: 11, color: 'var(--bm-text-muted)' }} className="mono">
          CONNECTED
        </span>
        <button
          type="button"
          className="bm-btn bm-btn-icon"
          title={theme === 'light' ? 'Switch to dark' : 'Switch to light'}
          onClick={onToggleTheme}
        >
          {theme === 'light' ? <Icon.moon size={15} /> : <Icon.sun size={15} />}
        </button>
      </div>
    </header>
  );
}

/* ─── Breadcrumb derivation ─────────────────────────────────────────── */

function buildBreadcrumb(pathname: string): BreadcrumbItem[] {
  if (pathname === '/' || pathname === '/dashboard') return [{ label: 'Dashboard' }];
  if (pathname === '/accounts') return [{ label: 'Accounts' }];
  if (pathname === '/activity') return [{ label: 'Recent activity' }];

  const bucketsMatch = /^\/accounts\/([^/]+)\/buckets$/.exec(pathname);
  if (bucketsMatch) {
    return [{ label: 'Accounts', to: '/accounts' }, { label: bucketsMatch[1] ?? '' }];
  }

  const objMatch = /^\/accounts\/([^/]+)\/buckets\/([^/]+)/.exec(pathname);
  if (objMatch) {
    const accountId = objMatch[1] ?? '';
    const bucketName = objMatch[2] ?? '';
    return [
      { label: 'Accounts', to: '/accounts' },
      { label: accountId, to: `/accounts/${accountId}/buckets` },
      { label: bucketName },
    ];
  }

  if (pathname.startsWith('/admin/settings')) return [{ label: 'Admin' }, { label: 'Settings' }];
  if (pathname.startsWith('/admin/audit')) return [{ label: 'Admin' }, { label: 'Audit log' }];
  if (pathname.startsWith('/admin/api-keys')) return [{ label: 'Admin' }, { label: 'API keys' }];

  return [];
}
